import { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Link } from 'react-router-dom';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { format, subDays, startOfDay } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShield, faUsers, faHome, faStar, faComments, faDatabase, faDownload, faFilter } from '@fortawesome/free-solid-svg-icons';
import { migrateContentToFirestore } from '../../utils/migrateContent';
import { migrateUnsplashData } from '../../utils/migrateUnsplashData';
import toast from 'react-hot-toast';

export default function AdminDashboard() {
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    totalFamilies: 0,
    totalExperiences: 0,
    totalConversations: 0,
    totalContent: 0,
    activeUsersLast7Days: 0,
    activeUsersLast30Days: 0
  });

  const [userGrowth, setUserGrowth] = useState([]);
  const [experiencesByType, setExperiencesByType] = useState([]);
  const [contentBreakdown, setContentBreakdown] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [migratingUnsplash, setMigratingUnsplash] = useState(false);
  const [dateRange, setDateRange] = useState('30');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [analyticsCache, setAnalyticsCache] = useState(null);
  const [cacheTimestamp, setCacheTimestamp] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  async function loadAnalytics() {
    try {
      // Fetch all collections
      const [users, families, experiences, conversations, content] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'families')),
        getDocs(collection(db, 'projects')),
        getDocs(collection(db, 'conversations')),
        getDocs(collection(db, 'content'))
      ]);

      // Calculate metrics
      const now = new Date();
      const sevenDaysAgo = subDays(now, 7);
      const thirtyDaysAgo = subDays(now, 30);

      const activeUsersLast7 = users.docs.filter(doc => {
        const user = doc.data();
        const lastActive = user.lastActive?.toDate() || user.createdAt?.toDate();
        return lastActive && lastActive > sevenDaysAgo;
      }).length;

      const activeUsersLast30 = users.docs.filter(doc => {
        const user = doc.data();
        const lastActive = user.lastActive?.toDate() || user.createdAt?.toDate();
        return lastActive && lastActive > thirtyDaysAgo;
      }).length;

      setMetrics({
        totalUsers: users.size,
        totalFamilies: families.size,
        totalExperiences: experiences.size,
        totalConversations: conversations.size,
        totalContent: content.size,
        activeUsersLast7Days: activeUsersLast7,
        activeUsersLast30Days: activeUsersLast30
      });

      // User growth over time (based on date range)
      const growthData = calculateUserGrowth(users.docs, dateRange);
      setUserGrowth(growthData);

      // Experiences by type
      const typeBreakdown = calculateExperiencesByType(experiences.docs);
      setExperiencesByType(typeBreakdown);

      // Content breakdown (recipes, activities, stories)
      const contentData = calculateContentBreakdown(content.docs);
      setContentBreakdown(contentData);

      // Recent activity feed
      const activity = await getRecentActivity();
      setRecentActivity(activity);

      // Cache results
      const cacheData = {
        dateRange,
        metrics: {
          totalUsers: users.size,
          totalFamilies: families.size,
          totalExperiences: experiences.size,
          totalConversations: conversations.size,
          totalContent: content.size,
          activeUsersLast7Days: activeUsersLast7,
          activeUsersLast30Days: activeUsersLast30
        },
        userGrowth: growthData,
        experiencesByType: typeBreakdown,
        contentBreakdown: contentData,
        recentActivity: activity
      };
      setAnalyticsCache(cacheData);
      setCacheTimestamp(Date.now());

      setLoading(false);
    } catch (error) {
      console.error('Error loading analytics:', error);
      setLoading(false);
    }
  }

  function calculateUserGrowth(userDocs, range = '30') {
    const days = range === 'all' ? 365 : parseInt(range);
    const dataPoints = Math.min(days, 30); // Max 30 data points for readability
    
    const interval = Math.ceil(days / dataPoints);
    const data = Array.from({ length: dataPoints }, (_, i) => {
      const date = subDays(new Date(), (dataPoints - 1 - i) * interval);
      return {
        date: format(date, range === 'all' ? 'MMM yyyy' : 'MMM dd'),
        users: 0
      };
    });

    userDocs.forEach(doc => {
      const createdAt = doc.data().createdAt?.toDate();
      if (createdAt) {
        const dayIndex = data.findIndex(day => {
          const dayDate = new Date(day.date);
          const createdDay = startOfDay(createdAt);
          const dataDay = startOfDay(dayDate);
          // Check if within the interval
          return createdDay >= dataDay && createdDay < subDays(dataDay, -interval);
        });
        if (dayIndex !== -1) {
          data[dayIndex].users++;
        }
      }
    });

    // Calculate cumulative
    let cumulative = 0;
    return data.map(day => {
      cumulative += day.users;
      return { ...day, cumulative };
    });
  }

  function calculateExperiencesByType(expDocs) {
    const types = {};
    expDocs.forEach(doc => {
      const type = doc.data().type || 'Other';
      types[type] = (types[type] || 0) + 1;
    });

    return Object.entries(types).map(([name, value]) => ({ name, value }));
  }

  function calculateContentBreakdown(contentDocs) {
    const breakdown = { recipes: 0, activities: 0, stories: 0 };
    contentDocs.forEach(doc => {
      const type = doc.data().type;
      if (breakdown.hasOwnProperty(type)) {
        breakdown[type]++;
      }
    });

    return [
      { name: 'Recipes', value: breakdown.recipes, color: '#2D5F5D' },
      { name: 'Activities', value: breakdown.activities, color: '#E8956B' },
      { name: 'Stories', value: breakdown.stories, color: '#4A8886' }
    ];
  }

  async function getRecentActivity() {
    try {
      const [recentUsers, recentExperiences, recentChats] = await Promise.all([
        getDocs(query(collection(db, 'users'), orderBy('createdAt', 'desc'), limit(5))),
        getDocs(query(collection(db, 'projects'), orderBy('createdAt', 'desc'), limit(5))),
        getDocs(query(collection(db, 'conversations'), orderBy('createdAt', 'desc'), limit(5)))
      ]);

      const activity = [];

      recentUsers.forEach(doc => {
        activity.push({
          type: 'user',
          icon: '👤',
          text: `New user signed up: ${doc.data().displayName || 'Anonymous'}`,
          timestamp: doc.data().createdAt
        });
      });

      recentExperiences.forEach(doc => {
        activity.push({
          type: 'experience',
          icon: '🎉',
          text: `New experience created: ${doc.data().title}`,
          timestamp: doc.data().createdAt
        });
      });

      recentChats.forEach(doc => {
        activity.push({
          type: 'chat',
          icon: '💬',
          text: `New conversation started: ${doc.data().title}`,
          timestamp: doc.data().createdAt
        });
      });

      // Sort by timestamp descending
      return activity
        .sort((a, b) => {
          const aTime = a.timestamp?.toDate ? a.timestamp.toDate().getTime() : 0;
          const bTime = b.timestamp?.toDate ? b.timestamp.toDate().getTime() : 0;
          return bTime - aTime;
        })
        .slice(0, 10);
    } catch (error) {
      console.error('Error getting recent activity:', error);
      return [];
    }
  }

  async function handleMigrateContent() {
    setMigrating(true);
    try {
      const success = await migrateContentToFirestore();
      if (success) {
        toast.success('Content migration completed successfully!');
        // Reload analytics to show new content
        await loadAnalytics();
      } else {
        toast.error('Migration was cancelled or failed');
      }
    } catch (error) {
      console.error('Migration error:', error);
      toast.error('Migration failed. Check console for details.');
    } finally {
      setMigrating(false);
    }
  }

  async function handleMigrateUnsplash() {
    if (!window.confirm('This will fetch full Unsplash photo objects for all existing content with Unsplash images. This may take a few minutes. Continue?')) {
      return;
    }

    setMigratingUnsplash(true);
    try {
      const result = await migrateUnsplashData();
      if (result) {
        toast.success(`Unsplash migration completed! Updated: ${result.updated}, Skipped: ${result.skipped}`);
        // Reload analytics
        await loadAnalytics();
      } else {
        toast.error('Unsplash migration failed');
      }
    } catch (error) {
      console.error('Unsplash migration error:', error);
      toast.error('Unsplash migration failed. Check console for details.');
    } finally {
      setMigratingUnsplash(false);
    }
  }

  function handleExportData() {
    const data = {
      metrics,
      userGrowth,
      experiencesByType,
      contentBreakdown,
      recentActivity,
      exportedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin-analytics-${format(new Date(), 'yyyy-MM-dd')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Analytics data exported');
  }

  if (loading) {
    return <AdminLoadingSkeleton />;
  }


  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-serif font-bold text-text-primary flex items-center gap-3">
                <FontAwesomeIcon icon={faShield} className="text-4xl text-primary" />
                Admin Dashboard
              </h1>
              <p className="text-text-secondary mt-1">
                Analytics, metrics, and platform management
              </p>
            </div>
            <div className="flex gap-3 items-center">
              <button
                onClick={() => setShowDateFilter(!showDateFilter)}
                className="bg-white border-2 border-border text-text-primary font-semibold px-4 py-2 rounded-xl hover:border-primary transition-all flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faFilter} />
                {dateRange} days
              </button>
              <button
                onClick={handleExportData}
                className="bg-white border-2 border-border text-text-primary font-semibold px-4 py-2 rounded-xl hover:border-primary transition-all flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faDownload} />
                Export
              </button>
              <button
                onClick={handleMigrateContent}
                disabled={migrating}
                className="bg-blue-100 text-blue-700 font-semibold px-4 py-2 rounded-xl hover:bg-blue-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faDatabase} />
                {migrating ? 'Migrating...' : 'Migrate Content'}
              </button>
              <button
                onClick={handleMigrateUnsplash}
                disabled={migratingUnsplash}
                className="bg-purple-100 text-purple-700 font-semibold px-4 py-2 rounded-xl hover:bg-purple-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faDatabase} />
                {migratingUnsplash ? 'Migrating...' : 'Migrate Unsplash'}
              </button>
              <Link
                to="/admin/submissions"
                className="bg-gray-100 text-text-primary font-semibold px-6 py-3 rounded-xl hover:bg-gray-200 transition-all"
              >
                Submissions
              </Link>
              <Link
                to="/admin/content"
                className="bg-gradient-to-r from-primary to-primary-light text-white font-semibold px-6 py-3 rounded-xl hover:shadow-lg transition-all"
              >
                Manage Content
              </Link>
              <Link
                to="/admin/users"
                className="bg-gray-100 text-text-primary font-semibold px-6 py-3 rounded-xl hover:bg-gray-200 transition-all"
              >
                Users
              </Link>
              <Link
                to="/admin/roles"
                className="bg-gray-100 text-text-primary font-semibold px-6 py-3 rounded-xl hover:bg-gray-200 transition-all"
              >
                Roles
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-8">
        {/* Date Range Filter */}
        {showDateFilter && (
          <div className="mb-6 bg-white rounded-2xl border border-border p-4 shadow-sm">
            <div className="flex items-center gap-4">
              <label className="text-sm font-semibold text-text-primary">Date Range:</label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-4 py-2 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="365">Last year</option>
                <option value="all">All time</option>
              </select>
            </div>
          </div>
        )}

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Total Users"
            value={metrics.totalUsers}
            subtitle={`${metrics.activeUsersLast7Days} active last 7 days`}
            icon={faUsers}
            color="from-blue-500 to-blue-600"
          />
          <MetricCard
            title="Total Families"
            value={metrics.totalFamilies}
            subtitle="Active family accounts"
            icon={faHome}
            color="from-green-500 to-green-600"
          />
          <MetricCard
            title="Experiences Created"
            value={metrics.totalExperiences}
            subtitle="All time"
            icon={faStar}
            color="from-purple-500 to-purple-600"
          />
          <MetricCard
            title="Conversations"
            value={metrics.totalConversations}
            subtitle="AI chat sessions"
            icon={faComments}
            color="from-primary to-primary-dark"
          />
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* User Growth Chart */}
          <div className="bg-white rounded-2xl border border-border p-6 shadow-sm">
            <h2 className="text-xl font-serif font-bold text-text-primary mb-4">
              User Growth {dateRange === 'all' ? '(All Time)' : `(Last ${dateRange} days)`}
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={userGrowth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="cumulative" 
                  stroke="#2D5F5D" 
                  strokeWidth={3}
                  name="Total Users"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Experiences by Type */}
          <div className="bg-white rounded-2xl border border-border p-6 shadow-sm">
            <h2 className="text-xl font-serif font-bold text-text-primary mb-4">
              Experiences by Type
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={experiencesByType}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#2D5F5D" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Content Breakdown Pie Chart */}
          <div className="bg-white rounded-2xl border border-border p-6 shadow-sm">
            <h2 className="text-xl font-serif font-bold text-text-primary mb-4">
              Content Library
            </h2>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={contentBreakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {contentBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 text-center">
              <p className="text-2xl font-bold text-text-primary">{metrics.totalContent}</p>
              <p className="text-sm text-text-secondary">Total content items</p>
            </div>
          </div>

          {/* Recent Activity Feed */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-border p-6 shadow-sm">
            <h2 className="text-xl font-serif font-bold text-text-primary mb-4">
              Recent Activity
            </h2>
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {recentActivity.length === 0 ? (
                <p className="text-text-secondary text-center py-8">No recent activity</p>
              ) : (
                recentActivity.map((activity, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-3 pb-3 border-b border-border last:border-0"
                  >
                    <span className="text-2xl">{activity.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-text-primary">{activity.text}</p>
                      <p className="text-xs text-text-secondary mt-1">
                        {activity.timestamp?.toDate ? activity.timestamp.toDate().toLocaleString() : 'Unknown date'}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* User Activity Table */}
        <div className="bg-white rounded-2xl border border-border p-6 shadow-sm">
          <h2 className="text-xl font-serif font-bold text-text-primary mb-4">
            User Engagement Metrics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 rounded-xl">
              <p className="text-sm text-text-secondary mb-1">Daily Active Users</p>
              <p className="text-3xl font-bold text-blue-600">
                {metrics.activeUsersLast7Days}
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-xl">
              <p className="text-sm text-text-secondary mb-1">Monthly Active Users</p>
              <p className="text-3xl font-bold text-green-600">
                {metrics.activeUsersLast30Days}
              </p>
            </div>
            <div className="p-4 bg-purple-50 rounded-xl">
              <p className="text-sm text-text-secondary mb-1">Avg. Experiences per Family</p>
              <p className="text-3xl font-bold text-purple-600">
                {metrics.totalFamilies > 0 
                  ? (metrics.totalExperiences / metrics.totalFamilies).toFixed(1)
                  : 0
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Metric Card Component
function MetricCard({ title, value, subtitle, icon, color }) {
  return (
    <div className="bg-white rounded-2xl border border-border p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center text-white`}>
          <FontAwesomeIcon icon={icon} className="w-6 h-6" />
        </div>
      </div>
      <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-1">
        {title}
      </h3>
      <p className="text-4xl font-bold text-text-primary mb-1">{value}</p>
      <p className="text-sm text-text-secondary">{subtitle}</p>
    </div>
  );
}

// Loading Skeleton
function AdminLoadingSkeleton() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-8 py-8">
        <div className="animate-pulse">
          <div className="h-12 bg-gray-200 rounded w-1/3 mb-8" />
          <div className="grid grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-gray-200 rounded-2xl" />
            ))}
          </div>
          <div className="h-96 bg-gray-200 rounded-2xl" />
        </div>
      </div>
    </div>
  );
}
